import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, CreditCard, Lock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { accountService, getErrorMessage } from '@/services';
import type { AccountInfo } from '@/services';
import { clsx } from 'clsx';

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'validation.required'),
  newPassword: z.string().min(6, 'validation.minLength'),
  confirmPassword: z.string().min(1, 'validation.required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'validation.passwordMismatch',
  path: ['confirmPassword'],
});

type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

export default function Account() {
  const { t } = useTranslation();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
  });

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const data = await accountService.getMe();
      setAccount(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitPassword = async (data: ChangePasswordForm) => {
    try {
      await accountService.changePassword(data);
      toast.success(t('account.passwordChanged'));
      reset();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const getStatusBadge = () => {
    if (!account) return null;
    switch (account.subscriptionStatus) {
      case 'active':
        return <span className="badge-success">{t('account.statusActive')}</span>;
      case 'expiring_soon':
        return <span className="badge-warning">{t('account.statusExpiringSoon')}</span>;
      case 'expired':
        return <span className="badge-error">{t('account.statusExpired')}</span>;
      case 'unlimited':
        return <span className="badge-success">{t('account.statusUnlimited')}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('account.title')}</h1>
        </div>
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('account.title')}</h1>
        <p className="text-dark-400 mt-1">{t('account.subtitle')}</p>
      </div>

      {/* Account Info Card */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <User className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('account.info')}</h2>
            <p className="text-dark-400 text-sm">{t('account.infoDesc')}</p>
          </div>
        </div>

        {account && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('auth.username')}</label>
              <p className="text-dark-100 font-medium">{account.username}</p>
            </div>
            <div>
              <label className="label">{t('auth.email')}</label>
              <p className="text-dark-100 font-medium">{account.email || '-'}</p>
            </div>
            <div>
              <label className="label">{t('account.role')}</label>
              <p className="text-dark-100 font-medium capitalize">{account.role}</p>
            </div>
            <div>
              <label className="label">{t('account.language')}</label>
              <p className="text-dark-100 font-medium">
                {account.language === 'it' ? 'Italiano' : 'English'}
              </p>
            </div>
            <div>
              <label className="label">{t('account.memberSince')}</label>
              <p className="text-dark-100 font-medium">
                {dayjs(account.created_at).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
            <div>
              <label className="label">{t('account.lastLogin')}</label>
              <p className="text-dark-100 font-medium">
                {account.last_login_at
                  ? dayjs(account.last_login_at).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Card - only for non-admin users */}
      {account?.role !== 'admin' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <CreditCard className="text-primary-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-dark-100">{t('account.subscription')}</h2>
              <p className="text-dark-400 text-sm">{t('account.subscriptionDesc')}</p>
            </div>
          </div>

          {account && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="label mb-0">{t('common.status')}:</label>
                {getStatusBadge()}
              </div>

              <div>
                <label className="label">{t('account.expiresAt')}</label>
                <p className="text-dark-100 font-medium">
                  {account.expires_at
                    ? dayjs(account.expires_at).format('DD/MM/YYYY')
                    : t('account.noExpirationSet')}
                </p>
              </div>

              {account.daysRemaining !== null && account.daysRemaining > 0 && (
                <div>
                  <label className="label">{t('account.daysRemaining')}</label>
                  <p className={clsx(
                    'font-bold text-lg',
                    account.daysRemaining <= 7 ? 'text-warning-600' : 'text-success-600'
                  )}>
                    {account.daysRemaining} {t('account.days')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Change Password Card */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Lock className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('account.changePassword')}</h2>
            <p className="text-dark-400 text-sm">{t('account.changePasswordDesc')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-4">
          <div>
            <label className="label">{t('account.currentPassword')}</label>
            <input
              type="password"
              className={errors.currentPassword ? 'input-error' : 'input'}
              {...register('currentPassword')}
            />
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-error-500">
                {t(errors.currentPassword.message || '')}
              </p>
            )}
          </div>

          <div>
            <label className="label">{t('account.newPassword')}</label>
            <input
              type="password"
              className={errors.newPassword ? 'input-error' : 'input'}
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-error-500">
                {t(errors.newPassword.message || '', { min: 6 })}
              </p>
            )}
          </div>

          <div>
            <label className="label">{t('account.confirmPassword')}</label>
            <input
              type="password"
              className={errors.confirmPassword ? 'input-error' : 'input'}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-error-500">
                {t(errors.confirmPassword.message || '')}
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center gap-2"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {t('account.updatePassword')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
